import type { CallbackDataParams } from 'echarts/types/dist/shared';

import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportsTranslations,
  Blueprint,
  DatasetBase,
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
  ReportsPieChartOptions,
} from '@core/reports-types';
import { datasetValueFormatterOptions } from '@core/reports-util';
import type { DurationPipe } from '@core/user-interface-legacy';
import { valueFormatter } from '@core/utils-legacy';

import { filterDatasetForPieChart } from '../pie-dataset';
import { funnelTooltipContextFactory } from './tooltip';

const getParamValue = (params: CallbackDataParams) => {
  const data = Array.isArray(params.data) ? params.data : [params.data];
  return <number>data[params.encode!.value[0]];
};

const funnelPieChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): ReportsPieChartOptions => {
  const labelSettings = {
    label: <ReportsPieChartOptions['series'][0]['label']>{
      show: dataset.isSingleDimension,
      position: 'outside',
      formatter: param => {
        const name = dataset.getCategoryNameById(param.name);
        const value = getParamValue(param);

        const formattedValue = valueFormatter(
          value,
          datasetValueFormatterOptions(dataset),
          translations.i18n,
        );

        return `${name}\n{pct|${formattedValue}}`;
      },
    },
  };

  return <ReportsPieChartOptions>{
    dataset: dataset.dataset,
    tooltip: {
      trigger: 'item',
      formatter: params => funnelTooltipContextFactory(dataset, translations, params),
    },
    series: [
      {
        type: 'pie',
        ...labelSettings,
      },
    ],
  };
};

export const funnelPieChartBlueprint = (
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): Blueprint<any> => ({
  match: {
    kind: ReportDashboardComponentKind.Funnel,
    viewMode: ReportDashboardComponentViewMode.Chart,
    chartType: ReportDashboardComponentChartType.Pie,
  },
  factory: ({ component, dataset, metric }) => {
    const filteredDataset = filterDatasetForPieChart(dataset);

    return {
      vis: funnelPieChart(component, filteredDataset, metric, translations, durationPipe),
      dataset: filteredDataset,
    };
  },
});
